import React, { useCallback, useMemo, useState } from 'react'

import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

// UI

import { Box } from '@chakra-ui/react'
import SystemVariableDetailModal from '../components/SystemVariableDetailModal'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

//Helper
import { camelToWords } from '@/utils/stringHelper'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import SystemVariableListHeader from '../components/SystemVariableListHeader'
import getSystemVariableTableColumns from '../constants/systemVariableTableColumns'
import useSystemVariable from '../useSystemVariable'
import { LoadingSection } from '@/components/Elements'

const SystemVariableListPage = () => {
  const { type } = useParams()
  const title = useMemo(() => (type === 'sisic' ? 'SISIC' : camelToWords(type)), [type])

  // Modal State
  const [modalDetail, setModalDetail] = useState({
    show: false,
    type,
    isCreate: true,
  })

  //Table State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:systemVariable_${type}`, {})
  const query = convertTableStateToQuery(storedTableState)

  // Hooks
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()
  const { useFetchAllSystemVariables, useRemoveSystemVariable, useUpdateSystemVariable } = useSystemVariable()
  const { data: systemVariablesResponse, isLoading } = useFetchAllSystemVariables(type, query)

  const removeSystemVariable = useRemoveSystemVariable(type)
  const updateSystemVariable = useUpdateSystemVariable(type)

  // Handler
  const handleAdd = () => {
    setModalDetail({
      show: true,
      type,
      isCreate: true,
      detail: {},
    })
  }

  const handleOnRowClick = (row) => {
    setModalDetail({
      show: true,
      type,
      isCreate: false,
      detail: row?.original,
    })
  }

  const handleDelete = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())

        await removeSystemVariable.mutateAsync(id)

        // Jump back to first Page
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, removeSystemVariable]
  )

  const handleActivate = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())
        await updateSystemVariable.mutateAsync({ id, data: { type: type, isDeleted: false } })
        // Jump back to first Page
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, type, updateSystemVariable]
  )

  const defaultColumns = getSystemVariableTableColumns({
    type,
    handleActivate,
    handleDelete,
  })

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <>
      <SystemVariableDetailModal modalDetail={modalDetail} setModalDetail={setModalDetail} />
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <SystemVariableListHeader title={title} type={type} handleAdd={handleAdd} />
        <Box p={5} overflow="scroll">
          <FullTable
            data={systemVariablesResponse?.data ? systemVariablesResponse.data : []}
            rowCount={systemVariablesResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleOnRowClick}
          />
        </Box>
      </Box>
    </>
  )
}

export default SystemVariableListPage
